import { useState, useEffect, useCallback } from "react";
import {
	useLocation,
	useNavigate,
} from "react-router-dom";
import styled from "styled-components";
import "@fontsource/montserrat";
import { useAuthProvider } from "../../../Providers/AuthProvider.js";
import { postWithDependencyInjection } from "../../../Providers/EndpointProvider.js";
import { FRONTEND_LINKS } from "../../../Constants/FrontendLinks.js";
import { MobergLogoSpinner } from "../../../Components/MobergLogoSpinner/MobergLogoSpinner"
import * as MdIcons from 'react-icons/md';
import { useWorkspacesProvider } from "../../../Providers/WorkspacesProvider";
import { useBackendLinksProvider } from "../../../Providers/BackendLinksProvider";
import { useOnMount } from "../../../Hooks/useOnMount.js";
import { MobergFontFamily } from "../../../Components/MobergFont/MobergFont";
import bgImg from "../Login/bgImg.png";
import darkLogoImg from "../Login/moberg-logo-dark.png";
import lightLogoImg from "../Login/moberg-logo-light.png"
import { useMobergTheme } from "../../../Providers/MobergThemeProvider"

const ChangePassword = ({ setNavPage }) => {
	const authProvider = useAuthProvider()
	const workspaceProvider = useWorkspacesProvider()
	const { LINKS } = useBackendLinksProvider()
	const { colorScheme } = useMobergTheme()
	const logoImg = colorScheme === "dark" ? lightLogoImg : darkLogoImg
	const [email, setEmail] = useState(undefined);
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false)
	const [disableChangePasswordButton, setDisableChangePasswordButton] = useState(true)
	const [showEmailInfoMessage, setShowEmailInfoMessage] = useState(false)

	let navigate = useNavigate();

	const query = new URLSearchParams(useLocation().search);
	const setupToken = query.get('token')

	useOnMount(() => {
		setNavPage(false)

		postWithDependencyInjection(setupToken, workspaceProvider.selectedWorkspace, LINKS.ACCOUNT.SETUP_INFO).then((data) => {
			setEmail(data.email)
		}).catch((e) => {
			alert('This password reset link is invalid or has expired. Please request a new one.')
			navigate(FRONTEND_LINKS.MAIN)
		})
	})

	const changePassword = useCallback((confirmPassword) => {
		let body = {
			new_password: confirmPassword
		}

		return postWithDependencyInjection(
			setupToken,
			workspaceProvider.selectedWorkspace,
			LINKS.ACCOUNT.RESET_PASSWORD,
			body
		)
	}, [setupToken, workspaceProvider.selectedWorkspace, LINKS])

	function handleSendEmail() {
		setDisableChangePasswordButton(true)
		setShowEmailInfoMessage(true)
	}

	function resetPage() {
		setDisableChangePasswordButton(false)
		setShowEmailInfoMessage(false)
	}

	useEffect(() => {
		if (password !== confirmPassword || !password || !confirmPassword) {
			setDisableChangePasswordButton(true)
		} else {
			setDisableChangePasswordButton(false)
		}
	}, [password, confirmPassword])

	function handleChangePassword(e) {
		const specialChars = /[`!@$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/;
		if (e) {
			e.preventDefault()
		}

		if (password !== confirmPassword) {
			alert('Passwords do not match.')
			return
		}

		if (password.includes('#')) {
			alert('Password may not contain a hastag.')
			return
		}

		if (!specialChars.test(password)) {
			alert('Password must contain at least one special character.')
			return
		}
		if (!(/[0-9]/.test(password))) {
			alert('Password must contain at least one number.')
			return
		}
		if (!(String(password).length >= 8)) {
			alert('Password must be at least 8 characters long')
			return
		}

		handleSendEmail()
		changePassword(confirmPassword).then((text) => {
			alert("Your password was changed. Please check your email for confirmation.")
			authProvider.logout()
			navigate(FRONTEND_LINKS.MAIN)
		}).catch(e => {
			alert(e)
			resetPage()
		})
	}

	function handleBackToLogin() {
		navigate(FRONTEND_LINKS.MAIN);
	}

	return !email ? (
		<LoadingWrapper>
			<LoadingContainer>
				<MobergLogoSpinner />
				<LoadingTitle>Loading...</LoadingTitle>
			</LoadingContainer>
		</LoadingWrapper>
	) : (
		<ClinicalWrapper>
			<CenteredChangePasswordBox>
				<ChangePasswordBox>
					<TopSection>
						<img src={logoImg} alt="Moberg Analytics" style={{ width: '90px', marginBottom: '16px' }} />
						<ChangePasswordHeading>Change password</ChangePasswordHeading>
					</TopSection>
					
					<CenterSection>
						<ChangePasswordForm>
							<InputBox>
								<InputLabel>Email</InputLabel>
								<input 
									type="email" 
									placeholder="Email" 
									disabled={true}
									value={email || ""} 
									onChange={(e) => setEmail(e.target.value)}
									style={{ backgroundColor: 'var(--color-input-bg)', color: 'var(--text-secondary-color)' }}
								/>
							</InputBox>
							
							<InputBox>
								<InputLabel>New password <RequiredStar>*</RequiredStar></InputLabel>
								<div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
									<input 
										type={showPassword ? "text" : "password"} 
										placeholder="New password" 
										value={password || ""} 
										onChange={(e) => setPassword(e.target.value)}
									/>
									<ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
										{showPassword ? <MdIcons.MdVisibilityOff size={22} /> : <MdIcons.MdVisibility size={22} />}
									</ShowPasswordIcon>
								</div>
							</InputBox>
							
							<InputBox>
								<InputLabel>Confirm password <RequiredStar>*</RequiredStar></InputLabel>
								<div style={{ flexDirection: "row", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
									<input 
										type={showPassword ? "text" : "password"} 
										placeholder="Confirm new password" 
										value={confirmPassword || ""} 
										onChange={(e) => setConfirmPassword(e.target.value)}
									/>
									<ShowPasswordIcon onClick={() => setShowPassword(!showPassword)}>
										{showPassword ? <MdIcons.MdVisibilityOff size={22} /> : <MdIcons.MdVisibility size={22} />}
									</ShowPasswordIcon>
								</div>
							</InputBox>
							
							<PasswordInfo>
								<strong>Password requirements:</strong> At least 8 characters, one number, and one special character.
							</PasswordInfo>
						</ChangePasswordForm>
					</CenterSection>
					
					<BottomSection>
						<ChangePasswordButton 
							type="button" 
							onClick={handleChangePassword}
							disabled={disableChangePasswordButton || showEmailInfoMessage}
							style={{ 
								opacity: (disableChangePasswordButton || showEmailInfoMessage) ? "0.5" : "1",
								cursor: (disableChangePasswordButton || showEmailInfoMessage) ? "not-allowed" : "pointer"
							}}
						>
							{showEmailInfoMessage ? (
								<div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
									<div style={{ 
										width: "16px", 
										height: "16px", 
										border: "2px solid #fff", 
										borderTop: "2px solid transparent", 
										borderRadius: "50%", 
										animation: "spin 1s linear infinite",
										marginRight: "8px"
									}}></div>
									Changing password...
								</div>
							) : (
								"Change password"
							)}
						</ChangePasswordButton>
						
						<BackButton onClick={handleBackToLogin}>
							<MdIcons.MdOutlineArrowRightAlt 
								size={20} 
								style={{ 
									marginRight: "8px",
									transform: "rotate(180deg)"
								}} 
							/>
							Back to sign in
						</BackButton>
					</BottomSection>
				</ChangePasswordBox>
			</CenteredChangePasswordBox>
		</ClinicalWrapper>
	);
};

const ClinicalWrapper = styled.div`
	background-image: url(${bgImg});
	background-position: center;
	background-size: cover;
	background-repeat: no-repeat;
	background-color: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;

	&::before {
		content: '';
		position: absolute;
		inset: 0;
		background: rgba(0, 0, 0, 0);
		[data-color-scheme="dark"] & {
			background: rgba(0, 0, 0, 0.75);
		}
	}

	& > * {
		position: relative;
		z-index: 1;
	}
`;

const CenteredChangePasswordBox = styled.div`
	z-index: 2;
	display: flex;
	align-items: center;
	justify-content: center;
	align-self: center;
	justify-self: center;
	height: auto;
	width: 100%;
	max-width: 450px;
	min-width: 400px;
	padding: 0 20px;
	margin: 0 auto;
	left: 0;
	top: 0;

	@media (max-width: 768px) {
		width: 90%;
		min-width: 280px;
		max-width: 400px;
	}

	@media (max-width: 480px) {
		width: 95%;
		min-width: 260px;
		max-width: 350px;
		padding: 0 10px;
	}
`;

const ChangePasswordBox = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	flex-direction: column;
	box-shadow: var(--modal-box-shadow);
	background: var(--color-surface-panel);
	border-radius: 4px;
	min-width: 350px;
	padding: 32px;
`;

const TopSection = styled.div`
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	padding-top: 0;
	margin-bottom: 16px;
	@media (max-width: 600px) {
		align-items: center;
		padding-top: 0;
		padding-bottom: 16px;
	}
`;

const CenterSection = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 0;
`;

const BottomSection = styled.div`
	width: 100%;
	padding-bottom: 24px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 24px;
	@media (max-width: 600px) {
		padding-bottom: 32px;
	}
`;

const ChangePasswordHeading = styled.h1`
	font-family: ${MobergFontFamily.HEADER};
	font-weight: 700;
	margin: 0;
	letter-spacing: 1px;
	font-size: 24px;
	color: var(--color-text-primary);
`;

const ChangePasswordForm = styled.form`
	width: 100%;
	max-width: 100%;
	margin: 0 auto;
	display: flex;
	flex-direction: column;
	gap: 24px;
	@media (max-width: 600px) {
		width: 100%;
		min-width: 0;
		gap: 20px;
	}
`;

const InputBox = styled.div`
	position: relative;
	margin-bottom: 0;
	input {
		width: 100%;
		padding: 12px 40px 12px 12px;
		border: 1.5px solid var(--color-border-default);
		border-radius: 4px;
		font-size: 14px;
		font-family: inherit;
		outline: none;
		transition: border 0.2s;
		background: var(--color-input-bg);
		color: var(--color-text-primary);
		&:focus {
			border-color: var(--color-accent-interactive);
		}
		&::-ms-reveal {
			display: none;
		}
		&:-webkit-autofill,
		&:-webkit-autofill:hover,
		&:-webkit-autofill:focus {
			-webkit-box-shadow: 0 0 0 1000px var(--color-input-bg) inset;
			-webkit-text-fill-color: var(--color-text-primary);
			caret-color: var(--color-text-primary);
			transition: background-color 5000s ease-in-out 0s;
		}
	}
`;

const InputLabel = styled.label`
	display: block;
	font-size: 16px;
	color: var(--color-text-primary);
	font-weight: 500;
	margin-bottom: 6px;
	margin-left: 2px;
	font-family: inherit;
`;

const ShowPasswordIcon = styled.span`
	position: absolute;
	right: 14px;
	cursor: pointer;
	font-size: 1.2rem;
	user-select: none;
	display: flex;
	align-items: center;
	color: var(--color-text-primary);
	opacity: 0.5;
`;

const PasswordInfo = styled.div`
	font-size: 11px;
	color: var(--text-secondary-color);
	background: var(--color-input-bg);
	padding: 8px 12px;
	border-radius: 4px;
	border-left: 3px solid var(--color-accent-interactive);
	margin-top: 4px;
`;

const ChangePasswordButton = styled.button`
	width: 100%;
	padding: 12px 0;
	background: var(--color-accent-interactive);
	color: #fff;
	font-size: 16px;
	font-weight: 600;
	border: none;
	border-radius: 4px;
	cursor: pointer;
	margin-top: 32px;
	transition: filter 0.2s;

	[data-color-scheme="dark"] & {
		background: #207DEA;
		color: #fff;
	}

	&:hover:not(:disabled) {
		filter: brightness(0.85);
	}
	&:disabled {
		background: var(--color-border-default);
		cursor: not-allowed;
	}
`;

const BackButton = styled.button`
	margin-top: 24px;
	display: flex;
	width: 100%;
	height: 40px;
	align-items: center;
	justify-content: center;
	padding: 8px 16px;
	border-radius: 4px;
	border: 1px solid var(--color-accent-interactive);
	background: transparent;
	color: var(--color-accent-interactive);
	font-weight: 500;
	font-size: 14px;
	transition: all 0.2s;
	cursor: pointer;
	align-self: center;
	
	&:hover {
		background: var(--color-accent-interactive);
		color: var(--color-hover-active-blue-text, #fff);
	}
`;

const LoadingWrapper = styled.div`
	background-color: var(--color-surface-primary);
	position: absolute;
	top: 0;
	left: 0;
	bottom: 0;
	right: 0;
	width: 100vw;
	height: 100vh;
	display: flex;
	align-items: center;
	justify-content: center;
`;

const LoadingContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	gap: 24px;
`;

const LoadingTitle = styled.h2`
	font-family: ${MobergFontFamily.HEADER};
	font-weight: 700;
	font-size: 20px;
	color: var(--color-text-primary);
	margin: 0;
	letter-spacing: 0.3px;
`;

const RequiredStar = styled.span`
	color: #e74c3c;
	font-weight: bold;
	margin-left: 2px;
`;

export default ChangePassword;

